/**
 * 메뉴 구조 오버라이드 적용기 — 코드 카탈로그 + DB 오버라이드 → 실제로 그릴 트리.
 *
 * ★ 설계: 코드가 카탈로그, DB 는 오버라이드.
 *   사이드바의 렌더링 코드는 건드리지 않는다. 같은 모양의 배열을 만들어 넘겨줄 뿐이다.
 *   오버라이드가 없거나(마이그레이션 전) 로드에 실패하면 코드 기본값이 그대로 나온다 —
 *   메뉴가 통째로 사라지는 사고를 구조적으로 막는다.
 *
 * 편집 범위:
 *   - 1depth(섹션) · 2depth(항목): 이름·링크·아이콘·소속·순서 변경, 직접 추가/삭제
 *   - 문서 템플릿(docEntries): 이름·링크·아이콘을 바꿀 수 있다.
 *     다만 묶음 구조를 전용 렌더러가 쥐고 있어 다른 섹션으로 옮기지는 못한다.
 *   - 대메뉴 건너 옮기기(CRM ↔ IT-CONSULTING): 섹션은 행의 area, 항목은 부모 섹션을 따른다
 *     (menuPlacement). 다른 대메뉴의 코드 메뉴는 이 레이아웃 카탈로그에 없으므로
 *     생성물(menuCodeDefaults)의 이름·아이콘·경로로 그린다.
 *
 * 돌려주는 섹션·항목에는 menuKey(권한 키)가 붙는다 — 옮겨 온 메뉴는 `crm:${섹션}:${항목}` 으로
 * 조합하면 엉뚱한 키가 되므로, 레이아웃은 권한 판정에 이 값을 먼저 쓴다.
 */
import { MENU_CODE_DEFAULTS, MENU_CODE_ORDER } from "./menuCodeDefaults";
import { SETTINGS_CODE_ORDER } from "./settingsMenus";
import { CODE_ITEM_KEYS, CODE_SECTION_KEYS, canCrossArea, homeAreaOf, makeMenuPlacement } from "./menuPlacement";

export type MenuOverride = {
  menu_key: string;
  area: "crm" | "automation" | "settings";
  label: string | null;
  path: string | null;
  parent_key: string | null;
  sort_order: number | null;
  is_custom: boolean;
  /** lucide 아이콘 이름(menuIcons 허용 목록). null = 코드 아이콘 그대로. */
  icon?: string | null;
  /** 페이지 헤더 제목(DESIGN-AI §19). null = 메뉴 이름을 제목으로. 20260928 마이그레이션 전에는 키 자체가 없다. */
  page_title?: string | null;
};

/** 사이드바 항목/섹션이 공통으로 갖는 최소 형태. 아이콘 등 나머지는 그대로 통과시킨다. */
type AnyItem = { key: string; label: string; path?: string; [k: string]: unknown };
/** 문서 템플릿 묶음 — 단일 항목이거나 children 을 가진 그룹이다. */
type AnyDocEntry = { key: string; label: string; path?: string; type?: string; children?: AnyDocEntry[]; [k: string]: unknown };
type AnySection = { key: string; label: string; items?: AnyItem[]; extraItems?: AnyItem[]; docEntries?: AnyDocEntry[]; [k: string]: unknown };

export const CUSTOM_KEY_PREFIX = "custom:";

/** 코드 순서를 1000 간격으로 깔아 두면, 사이에 끼워 넣는 계산이 정수로 떨어진다. */
const STEP = 1000;

// 설정 메뉴(settings:*)의 코드 순서는 settingsMenus 가 쥔다 — 같은 눈금에 이어 붙인다.
const CODE_ORDER_INDEX = new Map([...MENU_CODE_ORDER, ...SETTINGS_CODE_ORDER].map((key, i) => [key, i]));

/**
 * 메뉴의 정렬값 — 저장된 순서가 있으면 그것, 없으면 사이드바 코드 순서.
 *
 * ★ 사이드바(applyMenuOverrides)와 관리 화면(buildAdminTree)이 반드시 같은 값을 써야 한다.
 *   예전엔 사이드바는 "섹션만 센 index", 관리 화면은 "전체 키 index" 로 각자 계산해서
 *   저장값이 일부만 있을 때 두 화면의 순서가 어긋났다(사용자 신고: Project 그룹).
 *   저장값(0·1000·2000…)과 눈금을 맞추기 위해 코드 index 에도 STEP 을 곱한다 —
 *   코드에 새로 생긴 메뉴는 정렬을 다시 저장하기 전까지 맨 뒤에 붙는다.
 */
export function menuSortOrder(menuKey: string, saved: number | null | undefined): number {
  if (saved !== null && saved !== undefined) return saved;
  return (CODE_ORDER_INDEX.get(menuKey) ?? CODE_ORDER_INDEX.size) * STEP;
}

/** "crm" + 섹션키 → "crm:sales" / 섹션키 + 항목키 → "crm:sales:accounts" */
export function sectionMenuKey(area: string, sectionKey: string): string {
  return `${area}:${sectionKey}`;
}
export function itemMenuKey(area: string, sectionKey: string, itemKey: string): string {
  return `${area}:${sectionKey}:${itemKey}`;
}
/** 커스텀 키는 접두사가 붙어 있어 area 조합을 하지 않는다. */
function keyOf(built: string, rawKey: string): string {
  return rawKey.startsWith(CUSTOM_KEY_PREFIX) ? rawKey : built;
}

type Indexed = Map<string, MenuOverride>;

function indexOverrides(overrides: MenuOverride[]): Indexed {
  const map: Indexed = new Map();
  overrides.forEach((o) => map.set(o.menu_key, o));
  return map;
}

/**
 * 코드 섹션 배열에 오버라이드를 입혀 새 배열을 만든다. 원본은 건드리지 않는다.
 * @param sections 코드 카탈로그 (CRMLayout/AutomationLayout 의 MENU_SECTIONS)
 * @param overrides DB 오버라이드 (없으면 코드 그대로 반환)
 * @param area "crm" | "automation"
 * @param customIcon 직접 추가한 메뉴에 쓸 아이콘 (코드 항목의 아이콘은 그대로 유지)
 */
export function applyMenuOverrides<S extends AnySection>(
  sections: S[],
  overrides: MenuOverride[],
  area: "crm" | "automation",
  customIcon?: unknown,
  /** 아이콘 이름 → 렌더 요소. 목록에 없는 이름이면 null 을 돌려 코드 아이콘을 유지한다. */
  renderIcon?: (name: string) => unknown,
  /** 코드 라벨 → 표시 라벨. 다른 대메뉴에서 옮겨 온 메뉴의 이름에 쓴다(CRM 은 번역 맵). */
  resolveLabel: (menuKey: string, fallback: string) => string = (_k, f) => f
): S[] {
  if (!overrides.length) return sections;
  // 키는 테이블에서 유일하다 — 다른 대메뉴로 옮겨 간 행(area 가 다르다)도 이름·경로는 이 키의 것이다.
  const byKey = indexOverrides(overrides);
  const placement = makeMenuPlacement(overrides, sections.map((s) => sectionMenuKey(area, s.key)));
  const ownSectionKeys = new Set(sections.map((s) => sectionMenuKey(area, s.key)));
  const iconOf = (name?: string | null) => (name && renderIcon?.(name)) || undefined;

  // ── 1) 코드 항목을 평평하게 펴면서 오버라이드를 입힌다 ──────────────────────
  type Node = { menuKey: string; parentKey: string | null; order: number; item: AnyItem; bucket: "items" | "extraItems" };
  const sectionNodes: { menuKey: string; order: number; section: S }[] = [];
  const itemNodes: Node[] = [];

  sections.forEach((section) => {
    const sectionKey = sectionMenuKey(area, section.key);
    const so = byKey.get(sectionKey);
    // 다른 대메뉴로 옮겨 간 섹션은 여기 그리지 않는다(그쪽이 그린다).
    if (placement.sectionArea(sectionKey) === area) {
      sectionNodes.push({
        menuKey: sectionKey,
        order: menuSortOrder(sectionKey, so?.sort_order),
        section: { ...section, label: so?.label ?? section.label, menuKey: sectionKey } as S,
      });
    }
    (["items", "extraItems"] as const).forEach((bucket) => {
      (section[bucket] as AnyItem[] | undefined)?.forEach((item) => {
        const menuKey = itemMenuKey(area, section.key, item.key);
        const io = byKey.get(menuKey);
        // 다른 대메뉴의 섹션으로 옮겨 간 항목(또는 섹션째 옮겨 간 하위)은 그쪽이 그린다.
        const parentKey = io?.parent_key && placement.sectionArea(io.parent_key) ? io.parent_key : sectionKey;
        if (placement.sectionArea(parentKey) !== area) return;
        itemNodes.push({
          menuKey,
          parentKey,
          order: menuSortOrder(menuKey, io?.sort_order),
          bucket,
          item: {
            ...item,
            menuKey,
            label: io?.label ?? item.label,
            ...(io?.path ? { path: io.path } : {}),
            ...(io?.icon && renderIcon?.(io.icon) ? { icon: renderIcon(io.icon) } : {}),
            ...(io?.page_title ? { pageTitle: io.page_title } : {}),
          },
        });
      });
    });
  });

  // ── 1-b) 다른 대메뉴의 코드 메뉴 중 이쪽으로 옮겨 온 것 ──────────────────────
  // 이 레이아웃 카탈로그에는 없으니 생성물의 이름·아이콘·경로로 그린다.
  // key 는 전체 메뉴 키 — 이쪽 번역 맵·React key 와 부딪치지 않게.
  CODE_SECTION_KEYS.forEach((menuKey) => {
    if (homeAreaOf(menuKey) === area || ownSectionKeys.has(menuKey)) return;
    if (placement.sectionArea(menuKey) !== area) return;
    const code = MENU_CODE_DEFAULTS[menuKey] ?? {};
    const so = byKey.get(menuKey);
    sectionNodes.push({
      menuKey,
      order: menuSortOrder(menuKey, so?.sort_order),
      section: {
        key: menuKey, menuKey, items: [],
        label: so?.label || resolveLabel(menuKey, code.label ?? menuKey.split(":").slice(-1)[0]),
        ...(iconOf(code.icon) ? { icon: iconOf(code.icon) } : {}),
        ...(code.minRole ? { minRole: code.minRole } : {}),
      } as unknown as S,
    });
  });
  CODE_ITEM_KEYS.forEach((menuKey) => {
    if (homeAreaOf(menuKey) === area || !canCrossArea(menuKey)) return;
    if (placement.itemArea(menuKey) !== area) return;
    const code = MENU_CODE_DEFAULTS[menuKey] ?? {};
    const io = byKey.get(menuKey);
    itemNodes.push({
      menuKey,
      parentKey: placement.itemParent(menuKey),
      order: menuSortOrder(menuKey, io?.sort_order),
      bucket: "items",
      item: {
        key: menuKey, menuKey,
        label: io?.label || resolveLabel(menuKey, code.label ?? menuKey.split(":").slice(-1)[0]),
        path: io?.path || code.path || "/",
        icon: iconOf(io?.icon) ?? iconOf(code.icon) ?? customIcon,
        ...(code.minRole ? { minRole: code.minRole } : {}),
        ...(io?.page_title ? { pageTitle: io.page_title } : {}),
      } as AnyItem,
    });
  });

  // ── 2) 직접 추가한 메뉴를 얹는다 ──────────────────────────────────────────
  const known = new Set([...sectionNodes.map((n) => n.menuKey), ...itemNodes.map((n) => n.menuKey)]);
  overrides
    // 섹션은 행의 area, 하위는 부모 섹션의 대메뉴를 따른다 — 다른 대메뉴의 섹션에 넣은 커스텀 항목도 그쪽에 뜬다.
    .filter((o) => o.is_custom && !known.has(o.menu_key)
      && (o.parent_key ? placement.sectionArea(o.parent_key) === area : o.area === area))
    .forEach((o) => {
      const label = o.label || "(이름 없음)";
      if (!o.parent_key) {
        // 상위가 없으면 1depth(섹션)
        sectionNodes.push({
          menuKey: o.menu_key,
          order: menuSortOrder(o.menu_key, o.sort_order),
          section: { key: o.menu_key, menuKey: o.menu_key, label, items: [], __custom: true } as unknown as S,
        });
      } else {
        itemNodes.push({
          menuKey: o.menu_key,
          parentKey: o.parent_key,
          order: menuSortOrder(o.menu_key, o.sort_order),
          bucket: "items",
          item: {
            key: o.menu_key, menuKey: o.menu_key, label, path: o.path || "/",
            icon: (o.icon && renderIcon?.(o.icon)) || customIcon,
            ...(o.page_title ? { pageTitle: o.page_title } : {}),
            __custom: true,
          } as AnyItem,
        });
      }
    });

  // ── 3) 순서대로 다시 조립한다 ─────────────────────────────────────────────
  sectionNodes.sort((a, b) => a.order - b.order || a.menuKey.localeCompare(b.menuKey));
  const sectionKeys = new Set(sectionNodes.map((n) => n.menuKey));
  const childrenOf = new Map<string, Node[]>();
  itemNodes.forEach((node) => {
    // 없어진 섹션을 가리키면 원래 섹션으로 되돌린다(고아 방지 — 메뉴가 사라지면 안 된다).
    const parent = node.parentKey && sectionKeys.has(node.parentKey)
      ? node.parentKey
      : node.menuKey.split(":").slice(0, 2).join(":");
    if (!childrenOf.has(parent)) childrenOf.set(parent, []);
    childrenOf.get(parent)!.push(node);
  });
  childrenOf.forEach((list) => list.sort((a, b) => a.order - b.order || a.menuKey.localeCompare(b.menuKey)));

  return sectionNodes.map(({ menuKey, section }) => {
    const children = childrenOf.get(menuKey) || [];
    const next = { ...section } as S;
    // 원래 items 가 있던 섹션은 items 로, extraItems 만 있던 섹션은 extraItems 로 돌려준다.
    // 다른 섹션에서 옮겨 온 항목은 items 로 붙인다(전용 렌더러가 없는 일반 목록).
    const items = children.filter((c) => c.bucket === "items").map((c) => c.item);
    const extras = children.filter((c) => c.bucket === "extraItems").map((c) => c.item);
    // 문서 템플릿: 이름·링크만 갈아 끼운다(묶음 구조는 전용 렌더러가 쥐고 있어 건드리지 않는다).
    const docs = (section as AnySection).docEntries;
    if (docs) {
      const patchDoc = (entry: AnyDocEntry): AnyDocEntry => {
        const o = byKey.get(itemMenuKey(area, section.key, entry.key));
        const patched: AnyDocEntry = {
          ...entry,
          label: o?.label ?? entry.label,
          ...(o?.path ? { path: o.path, absolutePath: o.path } : {}),
          // 전용 렌더러는 기본 문서 아이콘을 쓰지만, 지정된 게 있으면 그걸 존중한다.
          ...(o?.icon && renderIcon?.(o.icon) ? { icon: renderIcon(o.icon) } : {}),
          ...(o?.page_title ? { pageTitle: o.page_title } : {}),
        };
        if (entry.children) patched.children = entry.children.map(patchDoc);
        return patched;
      };
      (next as AnySection).docEntries = docs.map(patchDoc);
    }
    if (section.items !== undefined || items.length) (next as AnySection).items = items;
    if (section.extraItems !== undefined || extras.length) (next as AnySection).extraItems = extras;
    return next;
  });
}
